// /apps/patient/src/pages/records/DocumentsTab.tsx

import { useState, useEffect } from 'react';
import { patientApiService } from '../../services/api';
import {
  AlertCircle, FileText, Download, ExternalLink, Calendar, File
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { isDemoEnvironment } from '@/config/platforms';

interface Document {
  id: number;
  created_at: number;
  document_name: string;
  file_url?: string;
  category?: string;
  description?: string;
  file_type?: string;
  file_size?: number;
  file?: {
    path: string;
    name: string;
    access: 'private' | 'public';
    size: number;
    mime: string;
    url?: string; // doesn't exist in production, everything is private, shouldn't use

  }
}

export function DocumentsTab() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await patientApiService.getMyDocuments();
      setDocuments(Array.isArray(response) ? response : []);
    } catch (err: any) {
      console.error('Error fetching documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileIcon = (fileType?: string) => {
    if (!fileType) return <File className="h-5 w-5 text-gray-500" />;
    if (fileType.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (fileType.includes('image')) return <File className="h-5 w-5 text-blue-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'lab_result':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'imaging':
        return 'text-purple-700 bg-purple-50 border-purple-200';
      case 'referral':
        return 'text-orange-700 bg-orange-50 border-orange-200';
      case 'insurance':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'consent':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const formatCategory = (category?: string) => {
    if (!category) return null;
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleDownload = async (doc: Document) => {
    if (!doc.file) return;
    try {
      await patientApiService.downloadAttachment(doc.file);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const handleView = async (doc: Document) => {
    if (!doc.file) return;
    let path;
    if(isDemoEnvironment()) {
      path = doc.file?.url
    }else{
      path = doc.file?.path
    }
    try {
      const url = await patientApiService.getAttachmentDownloadUrl(path);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
    }
  };

  if (loading) {
    return (
      <div className="mt-6">
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-20 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchDocuments}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="mt-8 text-center py-12 bg-white rounded-lg">
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No documents found</p>
        <p className="text-sm text-gray-400 mt-1">Your documents will appear here</p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-start gap-4">
            <div className="p-2 bg-gray-50 rounded-lg">
              {getFileIcon(doc.file_type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <h3 className="font-medium text-gray-900 truncate">
                  {doc.document_name}
                </h3>
                {doc.category && (
                  <span className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
                    getCategoryColor(doc.category)
                  )}>
                    {formatCategory(doc.category)}
                  </span>
                )}
              </div>

              {doc.description && (
                <p className="text-sm text-gray-600 mb-2">{doc.description}</p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                {doc.created_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(doc.created_at), 'MMM d, yyyy')}
                  </span>
                )}

                {doc.file_size && (
                  <span className="text-gray-400 text-xs">
                    {formatFileSize(doc.file_size)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {doc.file && (
                <>
                  <button
                    onClick={() => handleView(doc)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="View"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
